
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';

import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { UserDisplayProvider } from './context/UserDisplayContext';
import { processReferralFromUrl } from './utils/referralUtils';
import LanguagePopup from './components/general/LanguagePopup';
import NativeRouteGuard from './components/mobile/NativeRouteGuard';

// Import route functions
import { getPublicRoutes } from './components/routing/PublicRoutes';
import { getBackofficeRoutes } from './components/routing/BackofficeRoutes';
import { getAdminRoutes } from './components/routing/AdminRoutes';

//Import vercel analytics
import { Analytics } from '@vercel/analytics/react';


// Optimized QueryClient with aggressive caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Component to handle referral code monitoring
const ReferralMonitor = () => {
  useEffect(() => {
    // Process referral code on initial load
    processReferralFromUrl();
  }, []);

  return null;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <Router>
            <ReferralMonitor />
            <LanguageProvider>
              <ThemeProvider>
                <AuthProvider>
                  <UserDisplayProvider>
                    <LanguagePopup />
                    <NativeRouteGuard />
                    <Toaster />
                    <Sonner />
                    <Routes>
                      {getPublicRoutes()}
                      {getBackofficeRoutes()}
                      {getAdminRoutes()}
                    </Routes>
                  </UserDisplayProvider>
                </AuthProvider>
              </ThemeProvider>
            </LanguageProvider>
          </Router>
        </TooltipProvider>
        <Analytics />
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
